import { useEffect, useRef, useState } from 'react';
import type { ClientMsg, Lang, RoomSummary } from '../../../shared/src/types.js';
import { Dust, Embers, Smoke, Spotlight } from '../components/Atmosphere.js';
import { errorMessage } from '../errorCopy.js';
import { loadNickname, saveNickname, sanitizeNickname } from '../nickname.js';

// Rotating noir taglines under the logo. Replaces the old single static
// "НУАР · НЕОН · ПРЕДАТЕЛЬСТВО" line — we cycle through a handful of
// flavor lines instead, with a fade between each.
const TAGLINES: Record<Lang, string[]> = {
  ru: [
    'ОДИН ИЗ ВАС — ВРАГ',
    'СЕГОДНЯ КТО-ТО НЕ ПРОСНЁТСЯ',
    'НЕ ВЕРЬ НИКОМУ',
    'ТИХО · ГОРОД ЗАСЫПАЕТ',
    'ЦЕНА ЛЖИ — ЖИЗНЬ',
  ],
  uk: [
    'ОДИН З ВАС — ВОРОГ',
    'СЬОГОДНІ ХТОСЬ НЕ ПРОКИНЕТЬСЯ',
    'НЕ ВІР НІКОМУ',
    'ТИХО · МІСТО ЗАСИНАЄ',
    'ЦІНА БРЕХНІ — ЖИТТЯ',
  ],
};

// App now passes the structured error { code, message }; older callers may
// still pass a bare string. We accept both and normalize via `errInfo`.
type AppError = { code?: string; message?: string };

interface Props {
  t: Lang;
  setT: (t: Lang) => void;
  send: (msg: ClientMsg) => void;
  // Latest server error; we use it to bail out of the 'joining' state if the
  // join fails (e.g. NO_ROOM) and to render a localized inline reason.
  lastError?: AppError | string | null;
}

type Mode = 'idle' | 'host' | 'join' | 'joining';

// Normalize the lastError prop to a { code, message } pair regardless of
// whether App passed the new object shape or a legacy string.
function errInfo(e: Props['lastError']): { code?: string; message?: string } {
  if (!e) return {};
  if (typeof e === 'string') return { message: e };
  return { code: e.code, message: e.message };
}

export function Home({ t, setT, send, lastError }: Props) {
  const [mode, setMode] = useState<Mode>('idle');
  const [name, setName] = useState(() => loadNickname() ?? '');
  // `editingName` is true when the input is shown. We collapse it into a chip
  // ("ВЫ: NICK · сменить") once the player has a saved nick — set-once is the
  // intended flow. Tapping "сменить" reveals the input again.
  const [editingName, setEditingName] = useState(() => !loadNickname());
  const [code, setCode] = useState('');
  const [makePrivate, setMakePrivate] = useState(false);
  const [rooms, setRooms] = useState<RoomSummary[] | null>(null);
  // Pre-selected room from the directory — when set, "JOIN" goes straight in.
  const [selectedRoom, setSelectedRoom] = useState<RoomSummary | null>(null);
  // Cycling tagline index — bumped every few seconds for a moody vibe under
  // the logo. The cross-fade is pure CSS; we just bump the index here.
  const [taglineIdx, setTaglineIdx] = useState(0);
  // Synchronous re-entrancy latch. setMode('joining') is batched by React, so
  // a genuine double-tap can fire submit()/the room-row twice before the
  // buttons unmount and emit two create/join messages. This ref blocks the
  // second emit immediately; it's reset when a server error bounces us back.
  const submittingRef = useRef(false);
  useEffect(() => {
    const id = setInterval(() => {
      setTaglineIdx((i) => (i + 1) % TAGLINES[t].length);
    }, 3500);
    return () => clearInterval(id);
  }, [t]);

  // If a server error arrives while we're spinning on 'joining', drop back
  // to the 'join' input so the user can retry, and release the submit latch
  // so a fresh attempt is allowed. The inline hint explains what failed.
  useEffect(() => {
    if (lastError) {
      submittingRef.current = false;
      if (mode === 'joining') setMode('join');
    }
  }, [lastError]);

  // Pre-fill code from URL ?code=XXXXXX (QR/share links land here).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const c = params.get('code');
    if (c && /^\d{6}$/.test(c)) {
      setCode(c);
      setMode('join');
    }
  }, []);

  // Live-poll the room directory while we're idle/joining. Cheap (3s) — these
  // listings are tiny.
  useEffect(() => {
    if (mode === 'host' || mode === 'joining') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch('/api/rooms');
        if (!r.ok) return;
        const list: RoomSummary[] = await r.json();
        if (!cancelled) setRooms(list);
      } catch {
        // Network blip; keep last list.
      }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [mode]);

  // Persist nickname (sanitized) so a returning user doesn't retype it.
  useEffect(() => {
    if (name.trim()) saveNickname(name);
  }, [name]);

  const submit = () => {
    if (submittingRef.current) return;
    if (!name.trim()) return;
    if (mode === 'host') {
      submittingRef.current = true;
      send({ kind: 'host:create', name: name.trim(), private: makePrivate });
      setMode('joining');
      return;
    }
    // 'join' or selected from list
    const joinCode = selectedRoom?.code ?? code;
    if (!/^\d{6}$/.test(joinCode)) return;
    submittingRef.current = true;
    send({ kind: 'player:join', code: joinCode, name: name.trim() });
    setMode('joining');
  };

  const lobbyRooms = (rooms ?? []).filter((r) => r.phase === 'lobby');
  const err = errInfo(lastError);
  const joinDisabled = !name.trim() || code.length !== 6;

  return (
    <div className="scr scr-home">
      <div className="bg-noir" />
      <Smoke />
      <Dust count={22} />
      <Embers count={10} />
      <Spotlight />
      <div className="vig vignette-pulse" />

      <div className="lang-switch">
        <button className={`mono ${t === 'ru' ? 'on' : ''}`} onClick={() => setT('ru')}>RU</button>
        <span>·</span>
        <button className={`mono ${t === 'uk' ? 'on' : ''}`} onClick={() => setT('uk')}>UA</button>
      </div>

      <div className="home-logo">
        <div className="home-logo-mark">
          <svg viewBox="0 0 100 100" width="72" height="72" className="home-logo-svg">
            <circle className="home-logo-pulse" cx="50" cy="50" r="46" fill="none" stroke="#ff2a3c" strokeWidth="1" />
            <circle cx="50" cy="50" r="46" fill="#0a0a0c" stroke="#ff2a3c" strokeWidth="2" />
            <path d="M50 14 L74 86 L50 70 L26 86 Z" fill="#ff2a3c" />
            <circle cx="50" cy="48" r="6" fill="#0a0a0c" />
          </svg>
        </div>
        <h1 className="display home-title home-title-anim">
          <span aria-hidden className="home-title-ghost">MAFIA</span>
          <span>MAFIA</span>
        </h1>
        <div className="home-tagline" aria-live="polite">
          {TAGLINES[t].map((line, i) => (
            <span
              key={`${t}-${i}`}
              className={`home-sub sub home-tagline-line ${i === taglineIdx ? 'on' : ''}`}
            >
              {line}
            </span>
          ))}
        </div>
      </div>

      <div className="home-actions">
        {mode === 'joining' && (
          <div className="join-spinner">
            <span className="display dot-pulse" style={{ fontSize: 22 }}>
              ●●●
            </span>
            <div className="mono" style={{ marginTop: 8 }}>
              {t === 'ru' ? 'ПОДКЛЮЧАЕМСЯ…' : 'ПІДКЛЮЧАЄМОСЯ…'}
            </div>
          </div>
        )}

        {mode !== 'joining' && (
          editingName ? (
            <input
              value={name}
              onChange={(e) => setName(sanitizeNickname(e.target.value))}
              onKeyDown={(e) => {
                // Collapse to the chip only on an explicit Enter — a blur
                // (tapping away, dismissing the soft keyboard, opening the
                // suggestion bar) used to snap it shut mid-edit on mobile.
                if (e.key === 'Enter' && sanitizeNickname(name)) {
                  e.preventDefault();
                  setEditingName(false);
                }
              }}
              placeholder={t === 'ru' ? 'Ваш ник' : 'Ваш нік'}
              autoFocus={!loadNickname()}
              className="mono code-cell"
              style={{ width: '100%', padding: '0 14px', fontSize: 18, height: 56, textAlign: 'left' }}
            />
          ) : (
            <div className="nick-chip" role="button" tabIndex={0}
              onClick={() => setEditingName(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setEditingName(true); }}
            >
              <span className="sub" style={{ color: 'var(--bone-dim)', fontSize: 10, letterSpacing: '.18em' }}>
                {t === 'ru' ? 'ВЫ' : 'ВИ'}
              </span>
              <span className="display nick-chip-name">{name}</span>
              <span className="mono nick-chip-edit">
                {t === 'ru' ? 'СМЕНИТЬ' : 'ЗМІНИТИ'}
              </span>
            </div>
          )
        )}

        {mode === 'idle' && (
          <>
            {/* Room directory */}
            {lobbyRooms.length > 0 && (
              <div className="room-browser">
                <div className="sub" style={{ color: 'var(--bone-dim)', fontSize: 10, padding: '6px 2px' }}>
                  {t === 'ru' ? 'ОТКРЫТЫЕ КОМНАТЫ' : 'ВІДКРИТІ КІМНАТИ'} · {lobbyRooms.length}
                </div>
                {lobbyRooms.map((r) => (
                  <button
                    key={r.code}
                    className="room-row"
                    onClick={() => {
                      setSelectedRoom(r);
                      setCode(r.code);
                      if (name.trim()) {
                        if (submittingRef.current) return;
                        submittingRef.current = true;
                        send({ kind: 'player:join', code: r.code, name: name.trim() });
                        setMode('joining');
                      } else {
                        setMode('join');
                      }
                    }}
                  >
                    <span className="room-row-code mono">{r.code}</span>
                    <span className="room-row-name display">{r.hostName}</span>
                    <span className="room-row-count mono">
                      {r.playerCount} {t === 'ru' ? 'ИГРОКИ' : 'ГРАВЦІ'}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <button className="btn btn-blood" onClick={() => setMode('host')}>
              <span className="display">{t === 'ru' ? 'СОЗДАТЬ ИГРУ' : 'СТВОРИТИ ГРУ'}</span>
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path d="M3 9h12M9 3l6 6-6 6" stroke="#0a0a0c" strokeWidth="2.4" fill="none" strokeLinecap="square" />
              </svg>
            </button>
            <div className="sub mono" style={{ color: 'var(--bone-dim)', fontSize: 10, textAlign: 'center', marginTop: -8 }}>
              {t === 'ru' ? 'Ваш телефон ведёт стол · раздаёт роли' : 'Ваш телефон веде стіл · роздає ролі'}
            </div>
            <button className="btn btn-ghost" onClick={() => setMode('join')}>
              <span className="display">{t === 'ru' ? 'ВОЙТИ ПО КОДУ' : 'УВІЙТИ ЗА КОДОМ'}</span>
            </button>
            <div className="sub mono" style={{ color: 'var(--bone-dim)', fontSize: 10, textAlign: 'center', marginTop: -8 }}>
              {t === 'ru' ? 'Вы садитесь за стол игроком' : 'Ви сідаєте за стіл гравцем'}
            </div>
          </>
        )}

        {mode === 'join' && (
          <>
            <div className="code-row">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span key={i} className={`mono code-cell ${code[i] ? 'filled' : ''}`}>
                  {code[i] ?? '_'}
                </span>
              ))}
            </div>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                setSelectedRoom(null);
              }}
              placeholder="000000"
              autoFocus
              className="mono code-cell"
              style={{ width: '100%', textAlign: 'center', height: 56, fontSize: 22 }}
            />
            <button
              className="btn btn-blood"
              onClick={submit}
              disabled={joinDisabled}
            >
              <span className="display">{t === 'ru' ? 'ВОЙТИ' : 'УВІЙТИ'}</span>
            </button>
            {/* Inline reason: server rejection wins (localized via errorMessage),
                else a hint explaining why JOIN is greyed out. */}
            {err.message || err.code ? (
              <div className="code-hint sub mono" role="status" style={{ color: 'var(--poison-red)', fontSize: 11, textAlign: 'center' }}>
                {errorMessage(err.code, t, err.message ?? '')}
              </div>
            ) : joinDisabled ? (
              <div className="code-hint sub mono" style={{ color: 'var(--bone-dim)', fontSize: 11, textAlign: 'center' }}>
                {t === 'ru' ? 'Введите ник и 6-значный код' : 'Введіть нік і 6-значний код'}
              </div>
            ) : null}
            <button className="btn btn-ghost" onClick={() => { setMode('idle'); setCode(''); }}>
              <span className="display">{t === 'ru' ? 'НАЗАД' : 'НАЗАД'}</span>
            </button>
          </>
        )}

        {mode === 'host' && (
          <>
            <label className="private-toggle">
              <input
                type="checkbox"
                checked={makePrivate}
                onChange={(e) => setMakePrivate(e.target.checked)}
              />
              <span className="mono">
                {t === 'ru' ? 'ПРИВАТНАЯ КОМНАТА (НЕ В СПИСКЕ)' : 'ПРИВАТНА КІМНАТА (НЕ В СПИСКУ)'}
              </span>
            </label>
            <button className="btn btn-blood" onClick={submit} disabled={!name.trim()}>
              <span className="display">{t === 'ru' ? 'СОЗДАТЬ КОМНАТУ' : 'СТВОРИТИ КІМНАТУ'}</span>
            </button>
            <button className="btn btn-ghost" onClick={() => setMode('idle')}>
              <span className="display">{t === 'ru' ? 'НАЗАД' : 'НАЗАД'}</span>
            </button>
          </>
        )}
      </div>

      <div className="home-foot mono">
        <span>v0.1 · {t === 'ru' ? 'БРАУЗЕРНАЯ' : 'БРАУЗЕРНА'}</span>
        <span className="dot-pulse">●</span>
        <span>
          {rooms === null
            ? '…'
            : `${rooms.length} ${t === 'ru' ? 'КОМНАТ' : 'КІМНАТ'}`}
        </span>
        <span>·</span>
        <a
          href="?dev=1"
          className="home-dev-link"
          title={t === 'ru' ? 'Dev-режим: один хост и боты в одной вкладке' : 'Dev-режим: один хост і боти в одній вкладці'}
        >
          DEV
        </a>
      </div>
    </div>
  );
}
